import { useMemo } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { PAGE_ROUTE_KEYS, type PageRouteKey } from '../types/pageInstruction';

type RouteInstructionConfig = {
  pattern: string;
  pageRouteKey: PageRouteKey | string;
  programScoped?: boolean;
};

const GLOBAL_PAGE_INSTRUCTION_ROUTES: RouteInstructionConfig[] = [
  { pattern: '/superadmin/users', pageRouteKey: PAGE_ROUTE_KEYS.GLOBAL_USER_LIST },
  { pattern: '/superadmin/error-logs', pageRouteKey: PAGE_ROUTE_KEYS.ERROR_LOGS },
  { pattern: '/superadmin/audit-report', pageRouteKey: PAGE_ROUTE_KEYS.AUDIT_LOGS },
  { pattern: '/superadmin/application-settings/instruction-manager', pageRouteKey: PAGE_ROUTE_KEYS.INSTRUCTION_MANAGER },
  { pattern: '/superadmin/application-settings', pageRouteKey: PAGE_ROUTE_KEYS.ADMIN_SETTINGS },
  { pattern: '/superadmin/application-settings/email-templates', pageRouteKey: PAGE_ROUTE_KEYS.EMAIL_TEMPLATES },
  { pattern: '/superadmin/application-settings/legal-pages', pageRouteKey: PAGE_ROUTE_KEYS.LEGAL_PAGES_MANAGEMENT },
  { pattern: '/superadmin/application-settings/notification-engine', pageRouteKey: PAGE_ROUTE_KEYS.NOTIFICATION_ENGINE },
  { pattern: '/superadmin/holiday-calendars', pageRouteKey: PAGE_ROUTE_KEYS.HOLIDAY_CALENDARS },
  { pattern: '/superadmin/business-hours', pageRouteKey: PAGE_ROUTE_KEYS.BUSINESS_HOURS },
  { pattern: '/programs', pageRouteKey: PAGE_ROUTE_KEYS.PROGRAM_LIST },
  { pattern: '/programs/:programId/dashboard', pageRouteKey: PAGE_ROUTE_KEYS.PROGRAM_DASHBOARD, programScoped: true },
  { pattern: '/programs/:programId/operational-dashboard', pageRouteKey: PAGE_ROUTE_KEYS.OPERATIONAL_DASHBOARD, programScoped: true },
  { pattern: '/programs/:programId/roles', pageRouteKey: PAGE_ROUTE_KEYS.PROGRAM_ROLE_LIST, programScoped: true },
  { pattern: '/programs/:programId/permissions-matrix', pageRouteKey: PAGE_ROUTE_KEYS.ROLE_MAPPING, programScoped: true },
  { pattern: '/programs/:programId/permissions', pageRouteKey: PAGE_ROUTE_KEYS.ROLE_MAPPING, programScoped: true },
  { pattern: '/programs/:programId/forms/builder', pageRouteKey: PAGE_ROUTE_KEYS.FORM_BUILDER, programScoped: true },
  { pattern: '/programs/:programId/forms/:formId/builder', pageRouteKey: PAGE_ROUTE_KEYS.FORM_BUILDER, programScoped: true },
  { pattern: '/programs/:programId/forms', pageRouteKey: PAGE_ROUTE_KEYS.PROGRAM_FORMS, programScoped: true },
  { pattern: '/programs/:programId/workflow-debug', pageRouteKey: PAGE_ROUTE_KEYS.WORKFLOW_DEBUG, programScoped: true },
  { pattern: '/programs/:programId/workflow-templates', pageRouteKey: PAGE_ROUTE_KEYS.WORKFLOW_TEMPLATES, programScoped: true },
  { pattern: '/programs/:programId/workflow', pageRouteKey: PAGE_ROUTE_KEYS.WORKFLOW_SETUP, programScoped: true },
  { pattern: '/programs/:programId/automation-rules/review-queue', pageRouteKey: PAGE_ROUTE_KEYS.AUTOMATION_RULE_REVIEW_QUEUE, programScoped: true },
  { pattern: '/programs/:programId/automation-rules/:ruleId', pageRouteKey: PAGE_ROUTE_KEYS.AUTOMATION_RULE_DETAIL, programScoped: true },
  { pattern: '/programs/:programId/automation-rules', pageRouteKey: PAGE_ROUTE_KEYS.AUTOMATION_RULES, programScoped: true },
  { pattern: '/programs/:programId/automation-monitor', pageRouteKey: PAGE_ROUTE_KEYS.AUTOMATION_MONITOR, programScoped: true },
  { pattern: '/programs/:programId/reports', pageRouteKey: PAGE_ROUTE_KEYS.PROGRAM_REPORTS, programScoped: true },
  { pattern: '/programs/:programId/demo-guide', pageRouteKey: PAGE_ROUTE_KEYS.PROGRAM_DEMO_GUIDE, programScoped: true },
  { pattern: '/programs/:programId/insights', pageRouteKey: PAGE_ROUTE_KEYS.ACTIONABLE_INSIGHTS, programScoped: true },
  { pattern: '/programs/:programId/ui-settings', pageRouteKey: PAGE_ROUTE_KEYS.PROGRAM_UI_SETTINGS, programScoped: true },
  { pattern: '/programs/:programId/self-registration-settings', pageRouteKey: PAGE_ROUTE_KEYS.SELF_REGISTRATION_SETTINGS, programScoped: true },
  { pattern: '/programs/:programId/location-settings', pageRouteKey: PAGE_ROUTE_KEYS.PROGRAM_LOCATION_SETTINGS, programScoped: true },
  { pattern: '/programs/:programId/entity-inheritance', pageRouteKey: PAGE_ROUTE_KEYS.ENTITY_INHERITANCE, programScoped: true },
  { pattern: '/programs/:programId/document-requirements', pageRouteKey: PAGE_ROUTE_KEYS.DOCUMENT_REQUIREMENTS, programScoped: true },
  { pattern: '/programs/:programId/call-logs/new', pageRouteKey: PAGE_ROUTE_KEYS.CALL_LOG_FORM, programScoped: true },
  { pattern: '/programs/:programId/call-logs/analytics', pageRouteKey: PAGE_ROUTE_KEYS.CALL_LOG_ANALYTICS, programScoped: true },
  { pattern: '/programs/:programId/call-logs/follow-ups', pageRouteKey: PAGE_ROUTE_KEYS.CALL_LOG_FOLLOWUP_WORKLIST, programScoped: true },
  { pattern: '/programs/:programId/call-logs/:callLogId', pageRouteKey: PAGE_ROUTE_KEYS.CALL_LOG_FORM, programScoped: true },
  { pattern: '/programs/:programId/call-logs', pageRouteKey: PAGE_ROUTE_KEYS.CALL_LOG_LIST, programScoped: true },
  { pattern: '/programs/:programId/sla-escalations', pageRouteKey: PAGE_ROUTE_KEYS.SLA_ESCALATIONS, programScoped: true },
  { pattern: '/programs/:programId/eligibility-rulesets', pageRouteKey: PAGE_ROUTE_KEYS.ELIGIBILITY_RULESETS, programScoped: true },
  { pattern: '/programs/:programId/plans4model/create/:modelType', pageRouteKey: PAGE_ROUTE_KEYS.FINANCIAL_PLANS, programScoped: true },
  { pattern: '/programs/:programId/plans4model/:planId/edit', pageRouteKey: PAGE_ROUTE_KEYS.FINANCIAL_PLANS, programScoped: true },
  { pattern: '/programs/:programId/plans4model/:planId', pageRouteKey: PAGE_ROUTE_KEYS.FINANCIAL_PLANS, programScoped: true },
  { pattern: '/programs/:programId/plans4model', pageRouteKey: PAGE_ROUTE_KEYS.FINANCIAL_PLANS, programScoped: true },
  { pattern: '/programs/:programId/patients/:patientId/360', pageRouteKey: PAGE_ROUTE_KEYS.PATIENT_360, programScoped: true },
];

export interface ResolvedGlobalPageInstruction {
  pageRouteKey: PageRouteKey | string;
  programId?: string | null;
}

export function useResolvedGlobalPageInstruction(): ResolvedGlobalPageInstruction | null {
  const location = useLocation();
  const params = useParams<{ programId?: string }>();

  return useMemo(() => {
    for (const route of GLOBAL_PAGE_INSTRUCTION_ROUTES) {
      const match = matchPath({ path: route.pattern, end: true }, location.pathname);
      if (!match) {
        continue;
      }

      const programId = route.programScoped
        ? params.programId ?? match.params.programId ?? null
        : null;

      return {
        pageRouteKey: route.pageRouteKey,
        programId,
      };
    }

    return null;
  }, [location.pathname, params.programId]);
}
